import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  CircularProgress, 
  Alert, 
  Button, 
  LinearProgress,
  Card,
  CardContent,
  Fade
} from '@mui/material';
import { CheckCircle, Error, HourglassEmpty } from '@mui/icons-material';
import { paymentApi, PaymentTokenManager, PaymentVerificationResponse } from '../../services/paymentApi';
import { Pages } from '../../utils';
import './styles.scss';

interface PaymentProcessingState {
  status: 'polling' | 'success' | 'error' | 'timeout';
  message: string;
  error?: string;
  paymentData?: any;
  attempt: number;
  maxAttempts: number;
}

export const PaymentProcessingPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [state, setState] = useState<PaymentProcessingState>({
    status: 'polling',
    message: 'Verifying your payment...',
    attempt: 0,
    maxAttempts: 20
  });
  
  const pollingRef = useRef<boolean>(false);
  const mountedRef = useRef<boolean>(true);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!token) {
      setState({
        status: 'error',
        message: 'Invalid payment link',
        error: 'No payment token found in URL',
        attempt: 0,
        maxAttempts: 20
      });
      return;
    }

    // Store token for later use
    PaymentTokenManager.storeToken(token);
    
    // Start polling if not already started
    if (!pollingRef.current) {
      startPaymentPolling(token);
    }
  }, [token]);

  const startPaymentPolling = async (paymentToken: string) => {
    if (pollingRef.current) return;
    pollingRef.current = true;


    try {
      const result = await paymentApi.pollPaymentVerification(
        paymentToken,
        20, // maxAttempts
        3000, // 3 second intervals
        (attempt, maxAttempts) => {
          if (!mountedRef.current) return;
          
          setState(prev => ({
            ...prev,
            attempt,
            maxAttempts,
            message: `Verifying payment... (${attempt}/${maxAttempts})`
          }));
        }
      );

      if (!mountedRef.current) return;

      if (result.verified && result.payment) {
        
        // Store payment data for account setup
        PaymentTokenManager.storePaymentData(result.payment);
        
        setState({
          status: 'success',
          message: 'Payment verified successfully!',
          paymentData: result.payment,
          attempt: state.attempt,
          maxAttempts: state.maxAttempts
        });

        // Redirect to existing registration flow after a brief success display
        setTimeout(() => {
          if (mountedRef.current) {
            navigate('/registration?payment_verified=true');
          }
        }, 2000);

      } else {
        
        let userMessage = 'Payment verification failed';
        if (result.error?.includes('expired')) {
          userMessage = 'Payment link expired, please try again';
        } else if (result.error?.includes('not found')) {
          userMessage = 'Invalid payment, please contact support';
        } else if (result.error?.includes('already used')) {
          userMessage = 'Account already created, please login';
        } else if (result.error?.includes('timeout')) {
          userMessage = 'Payment verification timeout - please contact support if payment was completed';
        }

        setState({
          status: 'error',
          message: userMessage,
          error: result.error,
          attempt: state.attempt,
          maxAttempts: state.maxAttempts
        });
      }
    } catch (error: any) {
      
      if (!mountedRef.current) return;
      
      setState({
        status: 'error',
        message: 'An unexpected error occurred during payment verification',
        error: error.message,
        attempt: state.attempt,
        maxAttempts: state.maxAttempts
      });
    } finally {
      pollingRef.current = false;
    }
  };

  const handleRetry = () => {
    if (!token) return;
    
    setState({
      status: 'polling',
      message: 'Retrying payment verification...',
      attempt: 0,
      maxAttempts: 20
    });
    
    pollingRef.current = false;
    startPaymentPolling(token);
  };

  const handleGoToLogin = () => {
    PaymentTokenManager.clearPaymentData();
    navigate(Pages.LOGIN);
  };

  const handleContactSupport = () => {
    // You can implement this to open a support modal or redirect to support page
    window.open('mailto:support@homedesignguild.com?subject=Payment%20Verification%20Issue', '_blank');
  };

  const getProgressPercentage = () => {
    if (state.maxAttempts === 0) return 0;
    return Math.min((state.attempt / state.maxAttempts) * 100, 100);
  };

  const renderStatusIcon = () => {
    switch (state.status) {
      case 'polling':
        return <CircularProgress size={60} sx={{ color: '#00A06D' }} />;
      case 'success':
        return <CheckCircle sx={{ fontSize: 60, color: '#4caf50' }} />;
      case 'error':
      case 'timeout':
        return <Error sx={{ fontSize: 60, color: '#f44336' }} />;
      default:
        return <HourglassEmpty sx={{ fontSize: 60, color: '#ff9800' }} />;
    }
  };

  const renderActionButtons = () => {
    switch (state.status) {
      case 'error':
      case 'timeout':
        return (
          <Box sx={{ display: 'flex', gap: 2, mt: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleRetry}
              sx={{ minWidth: 120 }}
            >
              Retry Verification
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleGoToLogin}
              sx={{ minWidth: 120 }}
            >
              Go to Login
            </Button>
            <Button
              variant="text"
              color="info"
              onClick={handleContactSupport}
              sx={{ minWidth: 120 }}
            >
              Contact Support
            </Button>
          </Box>
        );
      case 'success':
        return (
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Redirecting to account setup...
            </Typography>
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Box className="payment-processing-page">
      <Box className="content-container">
        <Fade in={true} timeout={500}>
          <Card className="processing-card" elevation={3}>
            <CardContent sx={{ textAlign: 'center', p: 4 }}>
              
              {/* Status Icon */}
              <Box sx={{ mb: 3 }}>
                {renderStatusIcon()}
              </Box>

              {/* Main Message */}
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: '#1c1c1c' }}>
                {state.status === 'polling' && 'Processing Payment'}
                {state.status === 'success' && 'Payment Confirmed!'}
                {(state.status === 'error' || state.status === 'timeout') && 'Verification Issue'}
              </Typography>

              <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
                {state.message}
              </Typography>

              {/* Progress Bar for Polling */}
              {state.status === 'polling' && (
                <Box sx={{ mb: 3 }}>
                  <LinearProgress 
                    variant="determinate" 
                    value={getProgressPercentage()} 
                    sx={{ 
                      height: 8, 
                      borderRadius: 4,
                      backgroundColor: '#e0e0e0',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 4,
                        backgroundColor: '#1976d2'
                      }
                    }} 
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Attempt {state.attempt} of {state.maxAttempts}
                  </Typography>
                </Box>
              )}

              {/* Success Message */}
              {state.status === 'success' && state.paymentData && (
                <Alert severity="success" sx={{ mb: 3, textAlign: 'left' }}>
                  <Typography variant="body2">
                    <strong>Payment Details:</strong><br/>
                    Plan: {state.paymentData.productName || state.paymentData.productTier}<br/>
                    Amount: ${state.paymentData.paymentAmount}<br/>
                    Email: {state.paymentData.contactEmail}
                  </Typography>
                </Alert>
              )}

              {/* Error Message */}
              {(state.status === 'error' || state.status === 'timeout') && (
                <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
                  <Typography variant="body2">
                    {state.error && (
                      <>
                        <strong>Error Details:</strong><br/>
                        {state.error}
                      </>
                    )}
                  </Typography>
                </Alert>
              )}

              {/* Action Buttons */}
              {renderActionButtons()}

              {/* Help Text */}
              <Typography variant="caption" color="text.secondary" sx={{ mt: 4, display: 'block', fontStyle: 'italic' }}>
                This process may take up to 60 seconds. Please don't close this window.
              </Typography>

            </CardContent>
          </Card>
        </Fade>
      </Box>
    </Box>
  );
};
